import { AssetsService } from "@/services/admin-services";
import { useEditor, useNode } from "@craftjs/core";
import { PhotoIcon } from "@heroicons/react/24/outline";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    LinearProgress,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    styled,
} from "@mui/material";
import { AxiosProgressEvent, CanceledError } from "axios";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { FileDrop } from "react-file-drop";
import Settings from "./Settings";

export type MediaProps = {
    type: "image" | "video";
    src?: string;
    alt?: string;
    width: string;
    height: string;
    mediaSource: "upload" | "external";
};

const defaultProps = {
    type: "image",
    alt: "",
    src: "",
    width: "100%",
    height: "auto",
    isDirectLink: false,
};

interface VideoFrameProps {
    enabled: boolean;
}

const VideoContainer = styled("div")<VideoFrameProps>(({ enabled }) => ({
    width: "100%",
    position: "relative",
    paddingTop: "25px",
    paddingBottom: "56.25%",
    height: 0,
    "> iframe": {
        pointerEvents: enabled ? "none" : "auto",
        border: 0,
        position: "absolute",
        left: 0,
        top: 0,
        height: "100%",
        width: "100%",
    },
}));

const Media = ({ type = "image", src = "", alt = "", width, height }: Partial<MediaProps>) => {
    const {
        connectors: { connect },
        setProp,
    } = useNode();
    const { enabled } = useEditor((state) => ({ enabled: state.options.enabled }));

    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [externalSrc, setExternalSrc] = useState<string>("");
    const [externalSrcType, setExternalSrcType] = useState<string>(type);

    const [file, setFile] = useState<File | null>(null);
    const [filename, setFilename] = useState<string>("");
    const [fileType, setFileType] = useState<string>("");

    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const abortRef = useRef<AbortController | null>(null);

    const assetsService = useMemo(() => new AssetsService(), []);

    useEffect(() => {
        if (!file) return;
        setFilename(file.name);
        setFileType(file.type.split("/")[0]);
    }, [file]);

    useEffect(() => {
        if (!filename) return;
        onUpload();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filename]);

    const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (!files) return;
        setFile(files[0]);
    };

    const onFileSelect = () => {
        if (!inputRef.current) return;
        inputRef.current.click();
    };

    const onFileDrop = (files: FileList | null) => {
        if (!files) return;
        setFile(files[0]);
    };

    const onUpload = () => {
        if (!filename || !file) return;

        setIsSaving(true);

        const form = new FormData();
        form.append("Name", filename);
        form.append("File", file);

        const abort = new AbortController();
        abortRef.current = abort;

        assetsService
            .createWithProgress(form, abort, onUploadProgress)
            .then((response) => {
                setIsSaving(false);
                setProp((props) => {
                    props.src = response.file.url;
                    props.type = fileType;
                    props.mediaSource = false;
                });
                abortRef.current = null;
            })
            .catch((err) => {
                if (err instanceof CanceledError) return;
                throw err;
            });
    };

    const onUploadProgress = (event: AxiosProgressEvent) => {
        setUploadProgress((event.progress || 0) * 100);
    };

    // const onCancel = () => {
    //     abortRef.current?.abort();
    // };

    const addExternalMedia = () => {
        let width = "100%";
        let height = "auto";
        if (externalSrcType === "video") {
            width = "1210px";
            height = "681px";
        }

        setShowDialog(false);
        setProp((props) => {
            props.src = externalSrc;
            props.type = externalSrcType;
            props.mediaSource = true;
            props.width = width;
            props.height = height;
        });
        setExternalSrc("");
        setExternalSrcType("");
    };

    const closeDialog = () => {
        setShowDialog(false);
        setExternalSrc("");
        setExternalSrcType("");
    };

    const renderMedia = () => {
        if (!src || !type) return null;

        if (type === "image") {
            return <img src={src} alt={alt} style={{ width, height }} />;
        }

        if (type === "video") {
            if (src.includes("youtube") || src.includes("youtu.be")) {
                const videoId = src.split("v=")[1] || src.split("/").pop();

                return (
                    <VideoContainer enabled={enabled}>
                        <iframe
                            width={width}
                            height={height}
                            src={`https://www.youtube.com/embed/${videoId}`}
                            allowFullScreen
                        />
                    </VideoContainer>
                );
            } else if (src.includes("vimeo")) {
                const videoId = src.split("/").pop();

                return (
                    <VideoContainer enabled={enabled}>
                        <iframe
                            width={width}
                            height={height}
                            src={`https://player.vimeo.com/video/${videoId}`}
                            allowFullScreen
                        />
                    </VideoContainer>
                );
            } else if (src.includes("dailymotion")) {
                const videoId = src.split("/").pop();

                return (
                    <VideoContainer enabled={enabled}>
                        <iframe
                            width={width}
                            height={height}
                            src={`https://www.dailymotion.com/embed/video/${videoId}`}
                            allowFullScreen
                        />
                    </VideoContainer>
                );
            }

            return <video src={src} controls style={{ width, height }} />;
        }

        return null;
    };

    return Boolean(src) || !enabled ? (
        <div
            ref={(ref) => connect(ref!)}
            style={type == "video" ? { maxWidth: width, maxHeight: height, margin: "auto", minWidth: "100%", minHeight: "auto" } : {}}
        >
            {renderMedia()}
        </div>
    ) : (
        <Box
            component={"div"}
            ref={(ref: HTMLElement) => connect(ref!)}
            sx={{
                width: "100%",
                height: "auto",
                p: 2,
                background: "rgba(0, 0, 0, 0.05)",
                pointerEvents: isSaving ? "none" : "auto",
                borderRadius: 2,
            }}
        >
            <FileDrop onDrop={onFileDrop}>
                <Typography variant={"h5"} component={"div"} sx={{ display: "flex", alignItems: "center" }}>
                    <Box width={24} height={24} sx={{ mr: 1 }}>
                        <PhotoIcon className="h-6 w-6" />
                    </Box>
                    Media
                </Typography>

                <Typography variant={"body2"} sx={{ m: (theme) => theme.spacing(1, 0) }}>
                    Upload an image or video to use.
                </Typography>
                <input
                    value=""
                    style={{ visibility: "hidden", opacity: 0, height: 0, width: 0 }}
                    ref={inputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={onFileInputChange}
                />
                {isSaving ? (
                    <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 15, mt: 2 }} />
                ) : (
                    <>
                        <Button variant="contained" disabled={isSaving} sx={{ mr: 1, mt: 1 }} onClick={onFileSelect}>
                            Select file
                        </Button>
                        <Button
                            variant="outlined"
                            disabled={isSaving}
                            sx={{ mt: 1 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowDialog(true);
                            }}
                        >
                            Add from URL
                        </Button>
                        <Dialog
                            open={showDialog}
                            onClose={() => setShowDialog(false)}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                            maxWidth={"sm"}
                        >
                            <form onSubmit={addExternalMedia}>
                                <DialogTitle>Insert External Media</DialogTitle>
                                <DialogContent dividers>
                                    <FormControl>
                                        <FormLabel id="demo-radio-buttons-group-label">Type</FormLabel>
                                        <RadioGroup
                                            aria-labelledby="demo-controlled-radio-buttons-group"
                                            name="controlled-radio-buttons-group"
                                            value={externalSrcType}
                                            onChange={(e) => setExternalSrcType(e.target.value)}
                                        >
                                            <FormControlLabel value="image" control={<Radio />} label="Image" />
                                            <FormControlLabel value="video" control={<Radio />} label="Video" />
                                        </RadioGroup>
                                    </FormControl>

                                    <TextField
                                        autoFocus
                                        fullWidth
                                        value={externalSrc}
                                        onChange={(event) => setExternalSrc(event.target.value)}
                                        label={`URL`}
                                        type="text"
                                        variant="standard"
                                        sx={{ mb: 3 }}
                                    />
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => closeDialog()} disabled={isSaving}>
                                        Cancel
                                    </Button>
                                    <Button variant="contained" type="submit" disabled={isSaving}>
                                        Add
                                    </Button>
                                </DialogActions>
                            </form>
                        </Dialog>
                    </>
                )}
            </FileDrop>
        </Box>
    );
};

Media.craft = {
    displayName: "Media",
    props: defaultProps,
    rules: {
        canDrag: () => true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        canDrop: (targetNode: any) =>
            targetNode.data.name === "PageContentContainer" ||
            targetNode.data.name === "Div" ||
            targetNode.data.name === "Grid",
    },
    related: {
        settings: Settings,
    },
};

export default Media;
